/**
 * common/src/other/timezone.js
 *
 * Module centralisé de gestion et de conversion des fuseaux horaires métier.
 * Garantit des calculs et affichages strictement déterministes et indépendants
 * du fuseau horaire de l'appareil client ou du serveur hébergeur.
 */

export const DEFAULT_TIMEZONE = "Europe/Paris";

export const AVAILABLE_TIMEZONES = [
  // Europe
  { value: "Europe/Paris", label: "France / Monaco (Europe/Paris - UTC+1/+2)", group: "Europe" },
  { value: "Europe/London", label: "Royaume-Uni / Irlande (Europe/London - UTC+0/+1)", group: "Europe" },
  { value: "Europe/Brussels", label: "Belgique (Europe/Brussels - UTC+1/+2)", group: "Europe" },
  { value: "Europe/Zurich", label: "Suisse (Europe/Zurich - UTC+1/+2)", group: "Europe" },
  { value: "Europe/Berlin", label: "Allemagne (Europe/Berlin - UTC+1/+2)", group: "Europe" },
  { value: "Europe/Madrid", label: "Espagne (Europe/Madrid - UTC+1/+2)", group: "Europe" },
  { value: "Europe/Rome", label: "Italie (Europe/Rome - UTC+1/+2)", group: "Europe" },
  { value: "Europe/Amsterdam", label: "Pays-Bas (Europe/Amsterdam - UTC+1/+2)", group: "Europe" },
  { value: "Europe/Lisbon", label: "Portugal (Europe/Lisbon - UTC+0/+1)", group: "Europe" },
  { value: "Europe/Athens", label: "Grèce (Europe/Athens - UTC+2/+3)", group: "Europe" },
  { value: "Europe/Bucharest", label: "Roumanie (Europe/Bucharest - UTC+2/+3)", group: "Europe" },

  // France d'Outre-Mer (DOM-TOM)
  { value: "America/Guadeloupe", label: "Guadeloupe (America/Guadeloupe - UTC-4)", group: "France d'Outre-Mer" },
  { value: "America/Martinique", label: "Martinique (America/Martinique - UTC-4)", group: "France d'Outre-Mer" },
  { value: "America/Cayenne", label: "Guyane (America/Cayenne - UTC-3)", group: "France d'Outre-Mer" },
  { value: "Indian/Reunion", label: "La Réunion (Indian/Reunion - UTC+4)", group: "France d'Outre-Mer" },
  { value: "Indian/Mayotte", label: "Mayotte (Indian/Mayotte - UTC+3)", group: "France d'Outre-Mer" },
  { value: "Pacific/Tahiti", label: "Polynésie française (Pacific/Tahiti - UTC-10)", group: "France d'Outre-Mer" },
  { value: "Pacific/Noumea", label: "Nouvelle-Calédonie (Pacific/Noumea - UTC+11)", group: "France d'Outre-Mer" },

  // Amériques
  { value: "America/New_York", label: "États-Unis - New York (America/New_York - UTC-5/-4)", group: "Amériques" },
  { value: "America/Chicago", label: "États-Unis - Chicago (America/Chicago - UTC-6/-5)", group: "Amériques" },
  { value: "America/Denver", label: "États-Unis - Denver (America/Denver - UTC-7/-6)", group: "Amériques" },
  { value: "America/Los_Angeles", label: "États-Unis - Los Angeles (America/Los_Angeles - UTC-8/-7)", group: "Amériques" },
  { value: "America/Toronto", label: "Canada - Toronto / Montréal (America/Toronto - UTC-5/-4)", group: "Amériques" },

  // Afrique & Moyen-Orient
  { value: "Africa/Casablanca", label: "Maroc (Africa/Casablanca - UTC+1)", group: "Afrique & Moyen-Orient" },
  { value: "Africa/Tunis", label: "Tunisie (Africa/Tunis - UTC+1)", group: "Afrique & Moyen-Orient" },
  { value: "Africa/Algiers", label: "Algérie (Africa/Algiers - UTC+1)", group: "Afrique & Moyen-Orient" },
  { value: "Asia/Dubai", label: "Émirats Arabes Unis (Asia/Dubai - UTC+4)", group: "Afrique & Moyen-Orient" },
  { value: "Asia/Riyadh", label: "Arabie Saoudite (Asia/Riyadh - UTC+3)", group: "Afrique & Moyen-Orient" },

  // Asie & Pacifique
  { value: "Asia/Tokyo", label: "Japon (Asia/Tokyo - UTC+9)", group: "Asie & Pacifique" },
  { value: "Asia/Singapore", label: "Singapour (Asia/Singapore - UTC+8)", group: "Asie & Pacifique" },
  { value: "Australia/Sydney", label: "Australie - Sydney (Australia/Sydney - UTC+10/+11)", group: "Asie & Pacifique" }
];

const CET_ZONES = new Set([
  "Europe/Paris", "Europe/Berlin", "Europe/Madrid", "Europe/Rome",
  "Europe/Brussels", "Europe/Amsterdam", "Europe/Zurich"
]);
const WET_ZONES = new Set([
  "Europe/London", "Europe/Dublin", "Europe/Lisbon"
]);
const EET_ZONES = new Set([
  "Europe/Athens", "Europe/Bucharest", "Europe/Helsinki"
]);

const MONTH_NAMES_FR = ["janv.", "févr.", "mars", "avr.", "mai", "juin", "juil.", "août", "sept.", "oct.", "nov.", "déc."];
const MONTH_NAMES_FULL_FR = ["janvier", "février", "mars", "avril", "mai", "juin", "juillet", "août", "septembre", "octobre", "novembre", "décembre"];
const DAY_NAMES_FR = ["dim.", "lun.", "mar.", "mer.", "jeu.", "ven.", "sam."];
const DAY_NAMES_FULL_FR = ["dimanche", "lundi", "mardi", "mercredi", "jeudi", "vendredi", "samedi"];

const MONTH_NAMES_EN = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const MONTH_NAMES_FULL_EN = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
const DAY_NAMES_EN = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const DAY_NAMES_FULL_EN = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];

/**
 * Valide si une timezone IANA est valide.
 */
export function isValidTimezone(tz) {
  if (!tz || typeof tz !== "string") return false;
  try {
    new Intl.DateTimeFormat(undefined, { timeZone: tz });
    return true;
  } catch (_) {
    return AVAILABLE_TIMEZONES.some((item) => item.value === tz);
  }
}

/**
 * Extrait le fuseau horaire de l'instance depuis settings (avec repli Europe/Paris).
 */
export function getAppTimezone(settings) {
  const tz = settings?.timezone || settings?.timeZone;
  if (tz && typeof tz === "string" && tz.trim().length > 0) {
    return tz.trim();
  }
  return DEFAULT_TIMEZONE;
}

/**
 * Calcule l'instant du dernier dimanche du mois donné à hourUtc UTC.
 */
function getLastSundayUtc(year, month, hourUtc = 1) {
  const d = new Date(Date.UTC(year, month - 1, 31, hourUtc, 0, 0, 0));
  const dayOfWeek = d.getUTCDay();
  d.setUTCDate(31 - dayOfWeek);
  return d.getTime();
}

/**
 * Détermine si un instant UTC se trouve pendant l'heure d'été européenne (Directive 2000/84/CE).
 * Début : dernier dimanche de mars à 01:00 UTC.
 * Fin : dernier dimanche d'octobre à 01:00 UTC.
 */
export function isEuropeanDst(utcMillis) {
  const d = new Date(utcMillis);
  const year = d.getUTCFullYear();
  const marchTransition = getLastSundayUtc(year, 3, 1);
  const octoberTransition = getLastSundayUtc(year, 10, 1);
  return utcMillis >= marchTransition && utcMillis < octoberTransition;
}

/**
 * Calcule l'offset en minutes selon les règles strictes européennes.
 */
function getEuFallbackOffsetMinutes(utcMillis, tz) {
  const dst = isEuropeanDst(utcMillis);
  if (CET_ZONES.has(tz)) return dst ? 120 : 60;
  if (WET_ZONES.has(tz)) return dst ? 60 : 0;
  if (EET_ZONES.has(tz)) return dst ? 180 : 120;
  return null;
}

/**
 * Parse universellement n'importe quel instant vers un timestamp UTC en millisecondes.
 * Accepte ISO avec Z, ISO avec offset (+02:00), timestamp ms (number ou string), Date et objets Firestore.
 */
export function parseToUtcTimestamp(instant) {
  if (instant === null || instant === undefined || instant === "") return null;
  if (instant instanceof Date) {
    const t = instant.getTime();
    return Number.isNaN(t) ? null : t;
  }
  if (typeof instant === "number") {
    return Number.isFinite(instant) ? (instant < 10000000000 ? instant * 1000 : instant) : null;
  }
  if (typeof instant === "object") {
    if (typeof instant.toDate === "function") {
      const d = instant.toDate();
      return d instanceof Date && !Number.isNaN(d.getTime()) ? d.getTime() : null;
    }
    if (typeof instant.seconds === "number") return instant.seconds * 1000;
    if (typeof instant._seconds === "number") return instant._seconds * 1000;
  }
  if (typeof instant === "string") {
    const trimmed = instant.trim();
    if (!trimmed) return null;
    if (/^\d+$/.test(trimmed)) {
      const n = Number(trimmed);
      return n < 10000000000 ? n * 1000 : n;
    }
    const parsed = Date.parse(trimmed);
    if (!Number.isNaN(parsed)) return parsed;
  }
  return null;
}

/**
 * Extrait les composantes civiles (année, mois, jour, heure, minute, seconde)
 * dans le fuseau cible, avec repli explicite selon les exigences de Phase 2 :
 * 1. Intl.DateTimeFormat en premier.
 * 2. Si échec/indisponible : règle UE déterministe pour les zones CET/WET/EET.
 * 3. Sinon : repli explicite UTC (avec mention isFallbackUtc et log d'erreur).
 * JAMAIS le fuseau de l'appareil. JAMAIS de catch silencieux.
 */
function getWallClockPartsInTz(utcMillis, tz) {
  try {
    const formatter = new Intl.DateTimeFormat("en-US", {
      timeZone: tz,
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hour12: false
    });
    const parts = formatter.formatToParts(new Date(utcMillis));
    const p = {};
    for (const part of parts) p[part.type] = part.value;
    let hour = parseInt(p.hour, 10);
    if (hour === 24) hour = 0;
    return {
      year: parseInt(p.year, 10),
      month: parseInt(p.month, 10),
      day: parseInt(p.day, 10),
      hour: hour,
      minute: parseInt(p.minute, 10),
      second: parseInt(p.second, 10),
      isFallbackUtc: false
    };
  } catch (err) {
    const euOffset = getEuFallbackOffsetMinutes(utcMillis, tz);
    if (euOffset !== null) {
      const localMs = utcMillis + euOffset * 60000;
      const ld = new Date(localMs);
      return {
        year: ld.getUTCFullYear(),
        month: ld.getUTCMonth() + 1,
        day: ld.getUTCDate(),
        hour: ld.getUTCHours(),
        minute: ld.getUTCMinutes(),
        second: ld.getUTCSeconds(),
        isFallbackUtc: false
      };
    }

    console.error(`[timezone] Intl unsupported for timezone "${tz}". Falling back to explicit UTC.`);
    const ud = new Date(utcMillis);
    return {
      year: ud.getUTCFullYear(),
      month: ud.getUTCMonth() + 1,
      day: ud.getUTCDate(),
      hour: ud.getUTCHours(),
      minute: ud.getUTCMinutes(),
      second: ud.getUTCSeconds(),
      isFallbackUtc: true
    };
  }
}

/**
 * Formate un instant universel dans le fuseau cible selon le pattern et la locale donnés.
 * @param {string|number|Date|Object} instant - Instant universel
 * @param {string} tz - Fuseau IANA (ex: 'Europe/Paris')
 * @param {string} formatStr - Format standard ('lll', 'll', 'LT', 'L', 'YYYY-MM-DD', 'HH:mm', etc.)
 * @param {string} locale - 'fr' ou 'en'
 * @returns {string} Date et heure formatées
 */
export function formatInTz(instant, tz = DEFAULT_TIMEZONE, formatStr = "lll", locale = "fr") {
  const utcMillis = parseToUtcTimestamp(instant);
  if (utcMillis === null) return "";

  const resolvedTz = tz && typeof tz === "string" && tz.trim() ? tz.trim() : DEFAULT_TIMEZONE;
  const parts = getWallClockPartsInTz(utcMillis, resolvedTz);
  const isEn = typeof locale === "string" && locale.toLowerCase().startsWith("en");
  const pad2 = (n) => String(n).padStart(2, "0");

  const Y = parts.year;
  const M = parts.month;
  const D = parts.day;
  const H = parts.hour;
  const m = parts.minute;
  const s = parts.second;
  const dayOfWeek = new Date(Date.UTC(Y, M - 1, D, 12, 0, 0)).getUTCDay();

  let formatted = "";

  if (formatStr === "lll") {
    if (isEn) {
      const h12 = H % 12 || 12;
      const ampm = H >= 12 ? "PM" : "AM";
      formatted = `${MONTH_NAMES_EN[M - 1]} ${D}, ${Y} ${h12}:${pad2(m)} ${ampm}`;
    } else {
      formatted = `${D} ${MONTH_NAMES_FR[M - 1]} ${Y} ${pad2(H)}:${pad2(m)}`;
    }
  } else if (formatStr === "ll") {
    formatted = isEn ? `${MONTH_NAMES_EN[M - 1]} ${D}, ${Y}` : `${D} ${MONTH_NAMES_FR[M - 1]} ${Y}`;
  } else if (formatStr === "LT") {
    if (isEn) {
      const h12 = H % 12 || 12;
      const ampm = H >= 12 ? "PM" : "AM";
      formatted = `${h12}:${pad2(m)} ${ampm}`;
    } else {
      formatted = `${pad2(H)}:${pad2(m)}`;
    }
  } else if (formatStr === "L") {
    formatted = isEn ? `${pad2(M)}/${pad2(D)}/${Y}` : `${pad2(D)}/${pad2(M)}/${Y}`;
  } else {
    // 1. Extraire et masquer les littéraux entre crochets [texte] (norme Day.js / Moment)
    const literals = [];
    let masked = formatStr.replace(/\[([^\]]*)\]/g, (_, literalText) => {
      literals.push(literalText);
      return `\u0000__LITERAL_${literals.length - 1}__\u0000`;
    });

    // 2. Remplacer les tokens de formatage
    masked = masked.replace(/YYYY/g, String(Y));
    masked = masked.replace(/YY/g, String(Y).slice(-2));
    masked = masked.replace(/MMMM/g, isEn ? MONTH_NAMES_FULL_EN[M - 1] : MONTH_NAMES_FULL_FR[M - 1]);
    masked = masked.replace(/MMM/g, isEn ? MONTH_NAMES_EN[M - 1] : MONTH_NAMES_FR[M - 1]);
    masked = masked.replace(/MM/g, pad2(M));
    masked = masked.replace(/\bM\b/g, String(M));
    masked = masked.replace(/dddd/g, isEn ? DAY_NAMES_FULL_EN[dayOfWeek] : DAY_NAMES_FULL_FR[dayOfWeek]);
    masked = masked.replace(/ddd/g, isEn ? DAY_NAMES_EN[dayOfWeek] : DAY_NAMES_FR[dayOfWeek]);
    masked = masked.replace(/DD/g, pad2(D));
    masked = masked.replace(/\bD\b/g, String(D));
    const h12 = H % 12 || 12;
    const ampmUpper = H >= 12 ? "PM" : "AM";
    const ampmLower = H >= 12 ? "pm" : "am";
    masked = masked.replace(/HH/g, pad2(H));
    masked = masked.replace(/\bH\b/g, String(H));
    masked = masked.replace(/hh/g, pad2(h12));
    masked = masked.replace(/\bh\b/g, String(h12));
    masked = masked.replace(/mm/g, pad2(m));
    masked = masked.replace(/\bm\b/g, String(m));
    masked = masked.replace(/ss/g, pad2(s));
    masked = masked.replace(/\bs\b/g, String(s));
    masked = masked.replace(/\bA\b/g, ampmUpper);
    masked = masked.replace(/\ba\b/g, ampmLower);

    // 3. Restaurer les littéraux nettoyés de leurs crochets
    formatted = masked.replace(/\u0000__LITERAL_(\d+)__\u0000/g, (_, idx) => literals[Number(idx)]);
  }

  if (parts.isFallbackUtc) {
    formatted += " (UTC)";
  }

  return formatted;
}

/**
 * Convertit une heure civile saisie (depuis un picker ou une chaîne) dans le fuseau métier vers un instant UTC (ISO 8601 avec Z).
 * @param {string|Date|number} dateOrString - Valeur choisie par l'utilisateur
 * @param {string} tz - Fuseau de l'instance (ex: 'Europe/Paris')
 * @returns {string|null} Chaîne ISO UTC ("YYYY-MM-DDTHH:mm:ss.sssZ")
 */
export function localClockToUtc(dateOrString, tz = DEFAULT_TIMEZONE) {
  if (dateOrString === null || dateOrString === undefined || dateOrString === "") return null;

  let year, month, day, hour = 0, minute = 0, second = 0;

  if (typeof dateOrString === "string") {
    const str = dateOrString.trim();
    const match = str.match(/^(\d{4})-(\d{2})-(\d{2})(?:[T\s](\d{2}):(\d{2})(?::(\d{2}))?)?/);
    if (!match) {
      const p = Date.parse(str);
      if (Number.isNaN(p)) return null;
      return new Date(p).toISOString();
    }
    year = parseInt(match[1], 10);
    month = parseInt(match[2], 10);
    day = parseInt(match[3], 10);
    hour = match[4] ? parseInt(match[4], 10) : 0;
    minute = match[5] ? parseInt(match[5], 10) : 0;
    second = match[6] ? parseInt(match[6], 10) : 0;
  } else if (dateOrString instanceof Date) {
    if (Number.isNaN(dateOrString.getTime())) return null;
    year = dateOrString.getFullYear();
    month = dateOrString.getMonth() + 1;
    day = dateOrString.getDate();
    hour = dateOrString.getHours();
    minute = dateOrString.getMinutes();
    second = dateOrString.getSeconds();
  } else if (typeof dateOrString === "number") {
    return new Date(dateOrString).toISOString();
  } else if (typeof dateOrString === "object" && typeof dateOrString.format === "function") {
    // Objet Dayjs / Moment éventuel
    const formatted = dateOrString.format("YYYY-MM-DD HH:mm:ss");
    return localClockToUtc(formatted, tz);
  } else {
    return null;
  }

  const resolvedTz = tz && typeof tz === "string" && tz.trim() ? tz.trim() : DEFAULT_TIMEZONE;
  const guessUtc = Date.UTC(year, month - 1, day, hour, minute, second);

  // 1. Essai avec Intl.DateTimeFormat (affinement itératif 2 passes pour DST)
  try {
    const formatter = new Intl.DateTimeFormat("en-US", {
      timeZone: resolvedTz,
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hour12: false
    });

    let currentUtc = guessUtc;
    for (let i = 0; i < 2; i++) {
      const parts = formatter.formatToParts(new Date(currentUtc));
      const p = {};
      for (const part of parts) p[part.type] = part.value;
      let h = parseInt(p.hour, 10);
      if (h === 24) h = 0;
      const formattedLocalUtc = Date.UTC(
        parseInt(p.year, 10),
        parseInt(p.month, 10) - 1,
        parseInt(p.day, 10),
        h,
        parseInt(p.minute, 10),
        parseInt(p.second, 10)
      );
      const diff = guessUtc - formattedLocalUtc;
      if (diff === 0) break;
      currentUtc += diff;
    }
    return new Date(currentUtc).toISOString();
  } catch (err) {
    // 2. Règle UE déterministe
    const isParisOrCet = CET_ZONES.has(resolvedTz);
    const isWet = WET_ZONES.has(resolvedTz);
    const isEet = EET_ZONES.has(resolvedTz);

    if (isParisOrCet || isWet || isEet) {
      // Estimer DST pour la transition européenne
      const mDate = new Date(Date.UTC(year, 2, 31, 1, 0, 0));
      mDate.setUTCDate(31 - mDate.getUTCDay());
      const oDate = new Date(Date.UTC(year, 9, 31, 1, 0, 0));
      oDate.setUTCDate(31 - oDate.getUTCDay());
      const isDst = guessUtc >= mDate.getTime() && guessUtc < oDate.getTime();

      let offsetHours = 0;
      if (isParisOrCet) offsetHours = isDst ? 2 : 1;
      else if (isWet) offsetHours = isDst ? 1 : 0;
      else if (isEet) offsetHours = isDst ? 3 : 2;

      const utcMs = guessUtc - offsetHours * 3600000;
      return new Date(utcMs).toISOString();
    }

    console.error(`[localClockToUtc] Intl unsupported for timezone "${resolvedTz}". Falling back to UTC.`);
    return new Date(guessUtc).toISOString();
  }
}

// Support CommonJS pour compatibilité Node/Functions
if (typeof module !== "undefined" && module.exports) {
  module.exports = {
    DEFAULT_TIMEZONE,
    AVAILABLE_TIMEZONES,
    isValidTimezone,
    getAppTimezone,
    isEuropeanDst,
    parseToUtcTimestamp,
    formatInTz,
    localClockToUtc
  };
}
